import React, { useEffect, useState, useRef } from "react";
import Layout4 from "../../DataLayouts/Layout4";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import PersonIcon from "@mui/icons-material/Person";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import CategoryIcon from "@mui/icons-material/Category";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { MAIN_URL } from "../../../Configurations/Urls";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { operatorRequiresValue } from "../../../Components/filters/operators";

const MODULE = "intern_attendance_records";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_person_attendance_record_id",
  sortOrder: "desc",
};

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });
  return filters;
};

function InterAttendanceRecordList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const { userData } = useAuthStore();
  const org = userData?.organization;

  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState([]);
  const [tableHeaders, setTableHeaders] = useState([]);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);

  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const loadConfig = async () => {
      if (!org?.organization_id) return setLoadingConfig(false);

      try {
        const metaRes = await fetch(`${MAIN_URL}/api/metadata/${MODULE}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (metaRes.ok) {
          const meta = await metaRes.json();
          const fields = meta.fields || [];
          const displayFields = fields.filter((f) => f.is_display_field);
          const useFields = displayFields.length > 0 ? displayFields : fields;
          const cols = useFields.map((f) => ({
            field: f.field_key,
            label: f.label || f.field_key,
            visible: true,
            sortable: true,
            filterable: !!f.is_filterable,
            width: 150,
            field_type: f.field_type,
          }));
          setConfigColumns(cols);
          setTableHeaders(cols.map((c) => ({ name: c.label, value_key: c.field })));
        }
        const req = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const res = await req.json();
        const dg = res.datagrids || [];
        const key = `intern_attendance_records_grid_${org.organization_id}`;
        const saved = dg.find((x) => x.datagrid_key === key);
        if (saved?.datagrid_default_configuration) {
          setTableConfig(saved.datagrid_default_configuration);
          if (saved.datagrid_default_configuration.columns?.length > 0) {
            setConfigColumns((prev) => (prev.length ? prev : saved.datagrid_default_configuration.columns));
            setTableHeaders((prev) => (prev.length ? prev : saved.datagrid_default_configuration.columns.map((c) => ({ name: c.label || c.field, value_key: c.field || c.key }))));
          }
        }
      } catch (err) {
        console.error("Config load error:", err);
      }

      setLoadingConfig(false);
    };

    loadConfig();
  }, [org]);

  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const search = searchParams.get("search");
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "organization_person_attendance_record_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/intern-attendance-record-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data = [];
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            if (result.pagination) {
              const pageSize = result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page: result.pagination.page ?? Number.parseInt(searchParams.get("page"), 10) ?? 1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        setRows(data.map((item) => ({ ...item, id: item?.organization_person_attendance_record_id })));
      } catch (err) {
        if (err.name === "AbortError") return;

        if (err.response?.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          console.error("Failed to load intern attendance records", err);
          toast.error("Failed to load intern attendance records");
          setRows([]);
          setPaginationData(DEFAULT_PAGINATION);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org, searchParams]);

  return (
    <>
      <Layout4
        loading={loading}
        heading="Intern Attendance Records"
        btnName={null}
        Data={rows}
        edit_action="DISABLED"
        delete_action="DISABLED"
        add_action="DISABLED"
        tableHeaders={tableHeaders}
        Icons={[
          <PersonIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <FormatAlignJustifyIcon color="primary" />,
          <CategoryIcon sx={{ color: "text.secondary" }} />,
          <DateRangeIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "Intern Attendance Record",
          "Intern Attendance Record",
          "Add Intern Attendance Record",
          "Intern Attendance Record.",
        ]}
        Route="/intern/attendance/records"
        setData={setRows}
      />

      {!loadingConfig && (
        <TableDataGeneric
          tableName="intern_attendance_records"
          primaryKey="organization_person_attendance_record_id"
          heading="Intern Attendance Records"
          data={rows}
          sortname="attendance_date"
          showActions={false}
          Route="/intern/attendance/records"
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          filterModule={MODULE}
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default InterAttendanceRecordList;
